import React, { useCallback } from 'react';
import { Tooltip, Typography } from '@mui/material';
import type { LifecyclePhaseSlot } from '@features/lifecycles/lifecycle-groups.data';
import { Lifecycle } from '@shared/models/lifecycles.model';
import { usePhasesCardStyles } from './phases-card.styles';

type PhaseToggleProps = {
  label: LifecyclePhaseSlot['label'];
  description: LifecyclePhaseSlot['description'];
  lifecycle: Lifecycle | null;
  selected: boolean;
  groupActive: boolean;
  /** Allow tooltip activity to follow scope independently of the host's styling. */
  tooltipActive?: boolean;
  onSelectPhase: (lifecycle: Lifecycle) => void;
};

/** Single lifecycle phase toggle in the scope selector phase row. */
export const PhaseToggle = React.memo<PhaseToggleProps>(function PhaseToggle({
  label,
  description,
  lifecycle,
  selected,
  groupActive,
  tooltipActive = groupActive,
  onSelectPhase,
}) {
  const { classes, cx } = usePhasesCardStyles();
  const disabled = lifecycle === null;

  const handleToggle = useCallback(() => {
    if (lifecycle !== null) onSelectPhase(lifecycle);
  }, [lifecycle, onSelectPhase]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleToggle();
      }
    },
    [handleToggle],
  );

  if (disabled) {
    return (
      <Tooltip
        title={tooltipActive ? description : ''}
        placement="bottom"
        arrow
      >
        <Typography
          component="span"
          aria-disabled
          className={cx(classes.phase, groupActive && classes.phaseDisabled)}
        >
          {label}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={tooltipActive ? description : ''} placement="bottom" arrow>
      <Typography
        component="span"
        role="button"
        aria-pressed={selected}
        data-phase-selected={selected || undefined}
        tabIndex={0}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className={cx(
          classes.phase,
          groupActive && classes.phaseClickable,
          groupActive && !selected && classes.phaseInactive,
        )}
      >
        {label}
      </Typography>
    </Tooltip>
  );
});
